<script setup lang="ts">
    import { computed, onMounted, reactive } from 'vue';
    import { loadLists, createList, deleteLists, mergeLists } from './Lists.access';
    import type { List } from './ListsTypes.d';
    import { bsAlert, AlertTypes } from '@shared/components/BsAlerts';
    import ListComponent from '@shared/components/List/List.vue';
    import ListItem from './ListItem.vue';
    import { Permissions } from '@shared/Root/Permissions';
    import { setupTags } from '@shared/components/Tags/Tags';
    import { getWriteAccessDisabledIds } from '@cwd/Acl/useAclAccess';

    const orderOptions = [
        { display: $Resources.LastModifiedLabel, key: 'LastModified' },
        { display: $Resources.GenericCreatedLabel, key: 'Created' },
        { display: $Resources.ListsSortByNameLabel, key: 'Name' },
        { display: $Resources.ListsSortByContactCountLabel, key: 'ContactCount' },
    ];

    const state = reactive<{
        isBusy: boolean;
        lists: List[];
        selectedIds: string[];
    }>({
        isBusy: false,
        lists: [],
        selectedIds: [],
    });

    const tags = setupTags();

    const canMerge = computed(() => state.selectedIds.length >= 2);

    const aclDisabledIds = computed(() =>
        getWriteAccessDisabledIds(
            state.lists,
            (item) => item.ListId,
            (item) => item.CurrentUserAccessLevel
        )
    );

    onMounted(async () => {
        state.isBusy = true;
        state.lists = await loadLists().finally(() => (state.isBusy = false));
    });

    async function handleDelete(): Promise<void> {
        if (state.isBusy || state.selectedIds.length === 0) return;
        state.isBusy = true;
        try {
            const result = await deleteLists(state.selectedIds.map((id) => ({ ListId: id })));
            const deleted = new Set(result.DeletedIds);
            state.lists = state.lists.filter((l) => !deleted.has(l.ListId));
            state.selectedIds = [];
            if (result.DeletedIds.length > 0) {
                bsAlert();
            }
            for (const blocked of result.Blocked) {
                bsAlert({
                    type: AlertTypes.Danger,
                    title: $Resources.ErrorDeletingListsHeading,
                    content: `'${blocked.Name}'` + $Resources.ErrorDeletingListsWithTaskBody + blocked.BlockedBy,
                });
            }
        } catch {
            bsAlert({
                type: AlertTypes.Danger,
                title: $Resources.ErrorDeletingListsHeading,
                content: $Resources.ErrorDeletingListsBody,
            });
        } finally {
            state.isBusy = false;
        }
    }

    async function handleMerge(): Promise<void> {
        if (state.isBusy || state.selectedIds.length < 2) return;
        state.isBusy = true;
        const toMerge = new Set(state.selectedIds);
        try {
            const merged = await mergeLists(state.selectedIds.map((id) => ({ ListId: id })));
            // state.lists = state.lists.filter((l) => !toMerge.has(l.ListId));
            // state.lists.push(merged);
            state.selectedIds = [];
            window.location.href = `/Lists/${merged.ListId}?mergedList=true`;
        } finally {
            state.isBusy = false;
        }
    }

    async function handleCreate(): Promise<void> {
        if (state.isBusy) return;
        state.isBusy = true;
        try {
            const newList = await createList();
            window.location.href = `/Lists/${newList.ListId}?new=true`;
        } finally {
            state.isBusy = false;
        }
    }
</script>

<template>
    <ListComponent
        :items="state.lists"
        itemId="ListId"
        :title="$Resources.ListsListHeading"
        :description="$Resources.ListsListIntroduction"
        :filterKeys="['Name', 'Description']"
        :checkedItems="state.selectedIds"
        :disabledCheckboxes="aclDisabledIds"
        :loading="state.isBusy"
        :tags="tags"
        :orderOptions="orderOptions"
        :deleteConfirmation="true"
        :recycleManager="true"
        @update:checkedItems="(ids) => (state.selectedIds = ids as string[])"
        @clearSelected="state.selectedIds = []"
        @deleteSelected="handleDelete">
        <template #toolbar>
            <div v-require-scope="{ Feature: 'Lists', Permission: Permissions.Write }">
                <div v-show="state.selectedIds.length === 0">
                    <button type="button" class="btn btn-default" :disabled="state.isBusy" @click="handleCreate">
                        <span class="glyphicon glyphicon-plus m-xxs-r"></span>
                        {{ $Resources.ListsNewListButton }}
                    </button>
                </div>
            </div>
        </template>
        <template #toolbar-selected>
            <button v-if="canMerge" type="button" class="btn btn-default" :disabled="state.isBusy" @click="handleMerge">
                {{ $Resources.ListsMergeButton }} ({{ state.selectedIds.length }})
            </button>
        </template>
        <template #item="{ item }">
            <ListItem :item="item" :selectedTagIds="tags.state.selectedIds" @toggleTag="tags.toggle" />
        </template>
    </ListComponent>
</template>
